// Mobile Spaces: the list of spaces, and what's inside a space or folder (folders open in place, lists
// open their tasks). "All tasks" shows every task in the space or folder.
import { useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronRight, Folder, Layers, ListTodo } from '@/components/ui/icons';
import { useDb } from '@/lib/store';
import { useWorkspaceId } from '@/lib/nav';
import { foldersOf, listsOf, spacesOf } from '@/lib/hierarchy';
import { toggleExpanded, useUi } from '@/lib/ui';
import { useIsGuest } from '@/lib/roles';
import { SpaceAvatar } from '@/shell/WorkspaceLogo';
import { ShareButton } from '@/components/ShareButton';
import { MobileHeader } from './MobileShell';
import { NavRow, SearchBar, TopHeader } from './MobileHome';

export function MobileSpaces() {
  const ws = useWorkspaceId();
  const navigate = useNavigate();
  const guest = useIsGuest();
  const spaces = useDb((d) => spacesOf(d, ws).filter((s) => !s.archived));
  const name = useDb((d) => d.workspaces[ws]?.name);
  return (
    <div className="m-page">
      <TopHeader title="Spaces" />
      <div className="m-pad">
        <SearchBar />
      </div>
      <div className="m-scroll">
        {!guest && <NavRow icon={<span className="m-tile-icon"><Layers size={16} /></span>} label={<>All Tasks <span className="faint">– {name}</span></>} onClick={() => navigate(`/${ws}/everything`)} />}
        {spaces.map((s) => (
          <NavRow key={s.id} icon={<SpaceAvatar spaceId={s.id} size={26} />} label={s.name} chevron onClick={() => navigate(`/${ws}/s/${s.id}`)} />
        ))}
        {spaces.length === 0 && <div className="m-empty-line">{guest ? 'Nothing has been shared with you yet.' : 'No spaces yet.'}</div>}
      </div>
    </div>
  );
}

export function MobileTree({ type, id }: { type: 'space' | 'folder'; id: string }) {
  const ws = useWorkspaceId();
  const navigate = useNavigate();
  const expanded = useUi((s) => s.expanded);
  const space = useDb((d) => (type === 'space' ? d.spaces[id] : d.spaces[d.folders[id]?.spaceId ?? '']));
  const folder = useDb((d) => (type === 'folder' ? d.folders[id] : undefined));
  const db = useDb((d) => d);
  if (!space || (type === 'folder' && !folder)) {
    return (
      <div className="m-page">
        <MobileHeader title="Not found" />
        <div className="m-empty">
          <strong>This {type} isn’t available</strong>
          <span>It may have been deleted, or it isn’t shared with you.</span>
        </div>
      </div>
    );
  }
  const folders = type === 'space' ? foldersOf(db, space.id).filter((f) => !f.archived) : [];
  const lists = listsOf(db, space.id, type === 'folder' ? id : null).filter((l) => !l.archived);
  const listRow = (l: (typeof lists)[number]) => <NavRow key={l.id} icon={<ListTodo size={20} color={l.color ?? undefined} />} label={l.name} chevron onClick={() => navigate(`/${ws}/l/${l.id}`)} />;
  return (
    <div className="m-page">
      <MobileHeader
        icon={type === 'space' ? <SpaceAvatar spaceId={space.id} size={30} /> : <Folder size={22} color={folder!.color ?? undefined} />}
        title={type === 'space' ? space.name : folder!.name}
        sub={type === 'space' ? 'Space' : `in ${space.name}`}
        actions={type === 'folder' ? <ShareButton target={{ type: 'folder', id }} link={() => window.location.href} className="m-icon-btn m-share" iconSize={20} /> : undefined}
      />
      <div className="m-scroll">
        <NavRow icon={<span className="m-tile-icon"><Layers size={16} /></span>} label="All tasks" chevron onClick={() => navigate(`/${ws}/${type === 'space' ? 's' : 'f'}/${id}/tasks`)} />
        {folders.map((f) => {
          const open = expanded[`m:f:${f.id}`] ?? false;
          return (
            <div key={f.id}>
              <NavRow
                icon={<Folder size={20} color={f.color ?? undefined} />}
                label={f.name}
                extra={open ? <ChevronDown size={16} className="faint" /> : <ChevronRight size={16} className="faint" />}
                onClick={() => toggleExpanded(`m:f:${f.id}`, !open)}
              />
              {open && <div className="m-indent">{listsOf(db, space.id, f.id).filter((l) => !l.archived).map(listRow)}</div>}
            </div>
          );
        })}
        {lists.map(listRow)}
        {folders.length === 0 && lists.length === 0 && <div className="m-empty-line">Nothing here yet.</div>}
      </div>
    </div>
  );
}
